/**
 * @file lib/wellApi.js
 * @description Well Butler — thin client for the /api/v1/well/* engine routes.
 *
 * The well backend mounts at /api/v1/well (NOT /api), so these helpers prefix the
 * shared API base + '/v1/well'. Same cookie-SSO + transparent-refresh pattern as
 * lib/api.js (authFetch). Every call returns the server envelope { success, data,
 * meta, error } unchanged so the screens can show the answer + confidence + the
 * "you're the pro, verify it" disclaimer the engine already attaches.
 *
 * Endpoints (build step 7 target):
 *   POST /sizing       — size a pump            (engine MODE 1 sizePump)
 *   POST /diagnose     — troubleshoot           (elimination engine)
 *   POST /photo-audit  — photo-first system audit
 *   GET  /catalog      — browse the Hajoca catalog
 *   POST /order        — build a priced order
 *   POST /order/send   — send the order to the supply house (STUB channel)
 *
 * NOTE: there is no history list endpoint on the backend yet (see report). The
 * History screen reads a local cache of results this device produced.
 */
import { authFetch, API } from './api';

const WELL = `${API}/v1/well`;

/** POST a JSON body, return the raw envelope { success, data, meta, error }. */
async function post(path, body) {
  const res = await authFetch(`${WELL}${path}`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
  let json = null;
  try { json = await res.json(); } catch (_) { /* no body */ }
  // Normalize to the engine envelope shape even on a transport error.
  if (!json) {
    return { success: false, data: null, meta: {}, error: { code: 'NETWORK', message: `Request failed (${res.status})` } };
  }
  return json;
}

/** GET (query-string), return the raw envelope. */
async function get(path) {
  const res = await authFetch(`${WELL}${path}`);
  let json = null;
  try { json = await res.json(); } catch (_) { /* no body */ }
  if (!json) {
    return { success: false, data: null, meta: {}, error: { code: 'NETWORK', message: `Request failed (${res.status})` } };
  }
  return json;
}

export const wellApi = {
  /** Size a pump. input = { well, house, pressure, pipe, preferences, wellSiteId? } */
  sizePump: (input) => post('/sizing', input),

  /** Troubleshoot. input = { evidence, motor?, pumpType?, source?, ... } */
  diagnose: (input) => post('/diagnose', input),

  /** Photo audit. input = { imageBase64 | imageUrl, siteAuditId? } */
  photoAudit: (input) => post('/photo-audit', input),

  /** Browse the Hajoca catalog. q = { search?, category?, limit? } */
  browseCatalog: (q = {}) => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v != null && v !== '') params.append(k, v); });
    const qs = params.toString();
    return get(`/catalog${qs ? `?${qs}` : ''}`);
  },

  /** Build a priced order from line items. input = { lines, wellSiteId? } */
  buildOrder: (input) => post('/order', input),

  /** Send the order to the supply house (STUB channel logs it). */
  sendOrder: (input) => post('/order/send', input),

  /** Health ping (handy for connectivity checks). */
  health: () => get('/health'),
};

export default wellApi;
